import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
    Divider,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Chip,
    IconButton
} from '@mui/material';
import {
    Close as CloseIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Calculate as CalculateIcon,
    CreditCard as CreditCardIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import subscriptionApi from '../services/subscriptionApi';
import dayjs from 'dayjs';
import * as Sentry from "@sentry/react";

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount / 100); // Convert cents to dollars
};

const formatCredits = (credits) => {
    return new Intl.NumberFormat('en-US').format(credits);
};

const getDisplayPlanName = (planName) => {
    const name = planName.toLowerCase();
    if (name.includes('basic')) return 'Basic';
    if (name.includes('pro') && !name.includes('test')) return 'Growth';
    if (name.includes('enterprise')) return 'Pro';
    return planName;
};

const PlanChangePreview = ({ 
    open, 
    onClose, 
    currentPlan, 
    newPlan, 
    onConfirm,
    loading: parentLoading = false
}) => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load preview when dialog opens
    useEffect(() => {
        if (open && newPlan?.id) {
            loadPreview();
        }
    }, [open, newPlan?.id]);

    const loadPreview = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const previewData = await subscriptionApi.previewPlanChange(newPlan.id);
            setPreview(previewData);
        } catch (error) {
            Sentry.captureException(error);
            setError(error.message || 'Failed to load plan change preview');
            console.error('Error loading plan change preview:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = () => {
        if (onConfirm && preview) {
            onConfirm(preview);
        }
    };

    const handleClose = () => {
        if (!loading && !parentLoading) {
            onClose();
        }
    };

    const isUpgrade = newPlan?.price_cents > currentPlan?.price_cents;
    const isDowngrade = newPlan?.price_cents < currentPlan?.price_cents;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { 
                    borderRadius: 3,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalculateIcon />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Plan Change Preview
                    </Typography>
                </Box>
                <IconButton 
                    onClick={handleClose} 
                    disabled={loading || parentLoading}
                    sx={{ color: 'white' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {loading && (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        py: 8 
                    }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress size={60} sx={{ mb: 2 }} />
                            <Typography variant="body1" color="text.secondary">
                                Calculating costs...
                            </Typography>
                        </Box>
                    </Box>
                )}

                {error && (
                    <Box sx={{ p: 3 }}>
                        <Alert 
                            severity="error" 
                            onClose={() => setError(null)}
                            action={
                                <Button 
                                    color="inherit" 
                                    size="small" 
                                    onClick={loadPreview}
                                    disabled={loading}
                                >
                                    Retry
                                </Button>
                            }
                        >
                            {error}
                        </Alert>
                    </Box>
                )}

                {preview && !loading && (
                    <Box>
                        {/* Plan Comparison Section */}
                        <Box sx={{ p: 3, background: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Plan Comparison
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ height: '100%', border: '1px solid rgba(0,0,0,0.1)' }}>
                                        <CardContent>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                Current Plan
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                                {getDisplayPlanName(currentPlan?.name || '')}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary', mb: 2 }}>
                                                {formatCurrency(currentPlan?.price_cents || 0)}/month
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatCredits(currentPlan?.monthly_credits || 0)} credits/month
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card sx={{ 
                                        height: '100%',
                                        border: '2px solid #1976d2',
                                        background: 'linear-gradient(135deg, rgba(25,118,210,0.05) 0%, rgba(25,118,210,0.1) 100%)'
                                    }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    New Plan
                                                </Typography>
                                                {isUpgrade && (
                                                    <Chip 
                                                        label="UPGRADE" 
                                                        size="small" 
                                                        color="success"
                                                        icon={<TrendingUpIcon />}
                                                    />
                                                )}
                                                {isDowngrade && (
                                                    <Chip 
                                                        label="DOWNGRADE" 
                                                        size="small" 
                                                        color="warning"
                                                        icon={<TrendingDownIcon />}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                                                {getDisplayPlanName(newPlan?.name || '')}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                                                {formatCurrency(newPlan?.price_cents || 0)}/month
                                            </Typography>
                                            <Typography variant="body2" color="primary.main">
                                                {formatCredits(newPlan?.monthly_credits || 0)} credits/month
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        {/* Cost Breakdown Section */}
                        <Box sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Cost Breakdown
                            </Typography>

                            <List sx={{ p: 0 }}>
                                {preview.proration_credit > 0 && (
                                    <ListItem sx={{ px: 0, py: 1 }}>
                                        <ListItemText
                                            primary="Unused Time Credit"
                                            secondary="Credit for remaining time on current plan"
                                            primaryTypographyProps={{ fontWeight: 500 }}
                                        />
                                        <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                                            {formatCurrency(preview.proration_credit)}
                                        </Typography>
                                    </ListItem>
                                )}

                                <ListItem sx={{ px: 0, py: 1 }}>
                                    <ListItemText
                                        primary="New Plan Charge"
                                        secondary={`Prorated charge for ${getDisplayPlanName(newPlan?.name || '')} plan`}
                                        primaryTypographyProps={{ fontWeight: 500 }}
                                    />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(preview.proration_charge)}
                                    </Typography>
                                </ListItem>

                                <Divider sx={{ my: 2 }} />

                                <ListItem sx={{ px: 0, py: 1 }}>
                                    <ListItemText
                                        primary={
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {preview.immediate_charge > 0 ? 'Immediate Charge' : 'No Charge Today'}
                                            </Typography>
                                        }
                                        secondary={
                                            preview.immediate_charge > 0 
                                                ? "Amount to be charged now"
                                                : "Changes will apply at next billing cycle"
                                        }
                                    />
                                    <Typography 
                                        variant="h5" 
                                        sx={{ 
                                            fontWeight: 700,
                                            color: preview.immediate_charge > 0 ? 'primary.main' : 'success.main'
                                        }}
                                    >
                                        {preview.immediate_charge > 0 
                                            ? formatCurrency(preview.immediate_charge)
                                            : '$0.00'
                                        }
                                    </Typography>
                                </ListItem>
                            </List>
                        </Box>

                        <Divider />

                        {/* Additional Information */}
                        <Box sx={{ p: 3, background: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                What Happens Next
                            </Typography>

                            <List dense sx={{ p: 0 }}>
                                {preview.immediate_charge > 0 && (
                                    <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                                        <CheckCircleIcon sx={{ color: 'success.main', mr: 1, mt: 0.5 }} />
                                        <ListItemText
                                            primary="Immediate payment processing"
                                            secondary={`Your payment method will be charged ${formatCurrency(preview.immediate_charge)} now`}
                                        />
                                    </ListItem>
                                )}

                                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main', mr: 1, mt: 0.5 }} />
                                    <ListItemText
                                        primary="Additional credits"
                                        secondary={`${formatCredits(preview.credits_to_be_allocated)} extra credits will be added to your account`}
                                    />
                                </ListItem>

                                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                                    <ScheduleIcon sx={{ color: 'info.main', mr: 1, mt: 0.5 }} />
                                    <ListItemText
                                        primary="Next billing date"
                                        secondary={`Your next bill will be ${formatCurrency(newPlan?.price_cents || 0)} on ${dayjs(preview.next_billing_date).format('MMM D, YYYY')}`}
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    disabled={loading || parentLoading}
                    sx={{ borderRadius: 2 }}
                    size="large"
                >
                    Cancel
                </Button>
                
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={loading || parentLoading || !preview}
                    startIcon={
                        parentLoading ? <CircularProgress size={20} color="inherit" /> : 
                        preview?.immediate_charge > 0 ? <CreditCardIcon /> : <CheckCircleIcon />
                    }
                    sx={{
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        minWidth: 180
                    }}
                    size="large"
                >
                    {parentLoading ? 'Processing...' : 
                     preview?.immediate_charge > 0 
                        ? `Charge ${formatCurrency(preview.immediate_charge)}`
                        : 'Confirm Change'
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PlanChangePreview; 